import type { CollectionContact, CollectionPromise, CreditPolicy, Phase9Snapshot } from '../../types/domain';
import { DEFAULT_CREDIT_POLICY, PHASE9_DATABASE_KEY } from '../credit/credit-policy';

export const PHASE9_CHANGE_EVENT = 'nexo:phase9-changed';
type Phase9Database = { version: 1; businesses: Record<string, Phase9Snapshot> };
const emptyDatabase = (): Phase9Database => ({ version: 1, businesses: {} });
const id = (prefix: string) => `${prefix}_${crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`}`;
const nowIso = () => new Date().toISOString();

const readDatabase = (): Phase9Database => {
  try {
    const raw = localStorage.getItem(PHASE9_DATABASE_KEY);
    if (!raw) return emptyDatabase();
    const parsed = JSON.parse(raw) as Phase9Database;
    return parsed.version === 1 && parsed.businesses ? parsed : emptyDatabase();
  } catch {
    return emptyDatabase();
  }
};

const writeDatabase = (database: Phase9Database) => {
  localStorage.setItem(PHASE9_DATABASE_KEY, JSON.stringify(database));
  window.dispatchEvent(new CustomEvent(PHASE9_CHANGE_EVENT));
};

const emptySnapshot = (businessId: string): Phase9Snapshot => ({ businessId, creditPolicy: { ...DEFAULT_CREDIT_POLICY }, promises: [], contacts: [] });

export const ensurePhase9 = (businessId: string) => {
  const database = readDatabase();
  if (!database.businesses[businessId]) {
    database.businesses[businessId] = emptySnapshot(businessId);
    writeDatabase(database);
  }
  return database.businesses[businessId];
};

export const getPhase9 = (businessId: string) => readDatabase().businesses[businessId] ?? null;
export const subscribePhase9 = (listener: () => void) => {
  window.addEventListener(PHASE9_CHANGE_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => { window.removeEventListener(PHASE9_CHANGE_EVENT, listener); window.removeEventListener('storage', listener); };
};

const updateBusiness = (businessId: string, updater: (snapshot: Phase9Snapshot) => Phase9Snapshot) => {
  const database = readDatabase();
  const current = database.businesses[businessId] ?? emptySnapshot(businessId);
  database.businesses[businessId] = updater(current);
  writeDatabase(database);
  return database.businesses[businessId];
};

export const saveCreditPolicy = (businessId: string, policy: CreditPolicy) => updateBusiness(businessId, (snapshot) => ({
  ...snapshot,
  creditPolicy: {
    defaultDueDays: Math.max(1, Math.min(365, Math.round(policy.defaultDueDays))),
    overdueGraceDays: Math.max(0, Math.min(60, Math.round(policy.overdueGraceDays))),
    blockNewCreditWithOverdue: Boolean(policy.blockNewCreditWithOverdue),
    allowCreditWithoutLimit: Boolean(policy.allowCreditWithoutLimit),
    warnAtUtilizationPercent: Math.max(1, Math.min(100, Math.round(policy.warnAtUtilizationPercent))),
  },
})).creditPolicy;

export const createCollectionPromise = (businessId: string, input: Omit<CollectionPromise, 'id' | 'businessId' | 'status' | 'createdAt'>) => {
  if (!input.customerId || !input.receivableId) throw new Error('Selecciona una cuenta por cobrar.');
  if (!input.promisedDate) throw new Error('Indica la fecha prometida.');
  if (input.promisedAmountCents <= 0) throw new Error('El importe prometido debe ser mayor a cero.');
  const promise: CollectionPromise = { ...input, id: id('promise'), businessId, status: 'pending', createdAt: nowIso() };
  updateBusiness(businessId, (snapshot) => ({ ...snapshot, promises: [promise, ...snapshot.promises] }));
  return promise;
};

export const setCollectionPromiseStatus = (businessId: string, promiseId: string, status: CollectionPromise['status']) => {
  let updated!: CollectionPromise;
  updateBusiness(businessId, (snapshot) => ({
    ...snapshot,
    promises: snapshot.promises.map((promise) => {
      if (promise.id !== promiseId) return promise;
      updated = { ...promise, status, resolvedAt: status === 'pending' ? undefined : nowIso() };
      return updated;
    }),
  }));
  if (!updated) throw new Error('No se encontró la promesa de pago.');
  return updated;
};

export const recordCollectionContact = (businessId: string, input: Omit<CollectionContact, 'id' | 'businessId' | 'createdAt'>) => {
  if (!input.customerId) throw new Error('Selecciona un cliente.');
  const contact: CollectionContact = { ...input, id: id('contact'), businessId, createdAt: nowIso() };
  updateBusiness(businessId, (snapshot) => ({ ...snapshot, contacts: [contact, ...snapshot.contacts].slice(0, 2000) }));
  return contact;
};
